import { useState, useEffect, useRef } from 'react';
import WizardLayout from './WizardLayout';
import { useWizard } from '../../contexts/WizardContext';

import { useProfile } from '../../contexts/ProfileContext';
import { createMemoriesFromWizardStep, createWizardMemories } from '../../utils/wizardMemoryTemplates';
import norwegianKommuner from '../../data/norwegianKommuner';

export default function StepLocation({ navigate, embedded = false, draft: externalDraft, setDraft: setExternalDraft }) {
  // Use either external draft (for embedded mode) or context draft (for wizard mode)
  const wizardContext = useWizard();
  const { refresh } = useProfile();

  const draft = embedded ? externalDraft : wizardContext.draft;
  const setDraft = embedded ? setExternalDraft : wizardContext.setDraft;

  const [kommune, setKommune] = useState(draft?.kommune || '');
  const [kommuneInput, setKommuneInput] = useState(draft?.kommune || '');
  const [kommuneSuggestions, setKommuneSuggestions] = useState([]);
  const [showKommuneDropdown, setShowKommuneDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [schoolStage, setSchoolStage] = useState(draft?.schoolStage || '');
  const [schoolStageSuggested, setSchoolStageSuggested] = useState(false);
  const [, setSavedMemories] = useState([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const kommuneInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Keep local state in sync with draft changes (important for embedded mode)
  useEffect(() => {
    if (draft) {
      setKommune(draft.kommune || '');
      setKommuneInput(draft.kommune || '');
      setSchoolStage(draft.schoolStage || '');
    }
  }, [draft]);

  // Handle kommune input change and filtering
  const handleKommuneInputChange = (value) => {
    setKommuneInput(value);
    setHighlightedIndex(-1);

    if (value.trim().length > 0) {
      const filtered = norwegianKommuner.filter(k =>
        k.toLowerCase().startsWith(value.toLowerCase())
      );
      // Also include partial matches that don't start with the input
      const partialMatches = norwegianKommuner.filter(k =>
        !k.toLowerCase().startsWith(value.toLowerCase()) &&
        k.toLowerCase().includes(value.toLowerCase())
      );
      setKommuneSuggestions([...filtered, ...partialMatches].slice(0, 8));
      setShowKommuneDropdown(true);
    } else {
      setKommuneSuggestions([]);
      setShowKommuneDropdown(false);
    }

    // If input exactly matches a kommune, select it
    const exactMatch = norwegianKommuner.find(k => k.toLowerCase() === value.toLowerCase());
    if (exactMatch) {
      setKommune(exactMatch);
    } else {
      setKommune(''); // Clear the actual value if no exact match
    }
  };

  // Handle kommune selection from dropdown
  const handleKommuneSelect = (selectedKommune) => {
    setKommune(selectedKommune);
    setKommuneInput(selectedKommune);
    setShowKommuneDropdown(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation in dropdown
  const handleKommuneKeyDown = (e) => {
    if (!showKommuneDropdown || kommuneSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < kommuneSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleKommuneSelect(kommuneSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowKommuneDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        kommuneInputRef.current &&
        !kommuneInputRef.current.contains(event.target)
      ) {
        setShowKommuneDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stages = ['Barnehage', 'Barneskole', 'Ungdomsskole', 'Videregående'];

  // Auto-suggest school type from birth year (set once, user can override)
  useEffect(() => {
    if (schoolStageSuggested || schoolStage) return;
    const birthYear = draft?.child?.birthYear;
    if (!birthYear) return;

    const age = new Date().getFullYear() - birthYear;
    let suggested = '';
    if (age <= 5) suggested = 'Barnehage';
    else if (age <= 12) suggested = 'Barneskole';
    else if (age <= 15) suggested = 'Ungdomsskole';
    else if (age <= 18) suggested = 'Videregående';

    if (suggested) {
      setSchoolStage(suggested);
      setSchoolStageSuggested(true);
    }
  }, [draft?.child?.birthYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle changes to form fields
  const handleChange = () => {
    // Update the draft with current values
    const updatedDraft = {
      ...draft,
      kommune,
      schoolStage
    };
    
    // In embedded mode, just update the draft
    if (embedded) {
      setDraft(updatedDraft);
    }
  };

  // Update draft when form fields change
  useEffect(() => {
    if (embedded) {
      handleChange();
    }
  }, [kommune, schoolStage]); // eslint-disable-line react-hooks/exhaustive-deps

  const skipWizard = async () => {
    try {
      // Create memories if user has provided any location information
      if (kommune || schoolStage) {
        const stepData = {
          kommune: kommune || '',
          schoolStage: schoolStage || '',
          // Include child info from previous step for entity mapping
          alias: draft?.child?.alias || '',
          birthYear: draft?.child?.birthYear || null
        };
        
        const memories = createMemoriesFromWizardStep('location', stepData);
        
        if (memories.length > 0) {
          const result = await createWizardMemories(memories);
          if (result.success) {
            setSavedMemories(memories);
            setShowSaveConfirmation(true);
            setTimeout(() => setShowSaveConfirmation(false), 3000);
          }
        }
      }

      // Update profile with wizardSkipped flag (comment out profile save for now)
      // TODO: Remove profile saving entirely once migration is complete
      /*
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          wizardSkipped: true,
          kommune: kommune || '',
          schoolStage: schoolStage || ''
        })
      });
      */
      
      // Wait until shared context is up-to-date
      await refresh();
      
      // Guard now sees correct flags
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error skipping wizard:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  const onNext = async ({ skip } = {}) => {
    if (embedded) return;
    
    if (skip) {
      await skipWizard();
      return;
    }
    
    try {
      // Create memories from current step data
      const stepData = {
        kommune,
        schoolStage,
        // Include child info from previous step for entity mapping
        alias: draft?.child?.alias || '',
        birthYear: draft?.child?.birthYear || null
      };
      
      const memories = createMemoriesFromWizardStep('location', stepData);
      
      if (memories.length > 0) {
        const result = await createWizardMemories(memories);
        if (result.success) {
          setSavedMemories(memories);
          setShowSaveConfirmation(true);
          setTimeout(() => setShowSaveConfirmation(false), 3000);
        }
      }
      
      // Update local wizard state for flow continuity
      setDraft({
        ...draft,
        kommune,
        schoolStage
      });
      
      navigate('/onboarding/challenges');
    } catch (error) {
      console.error('Error in onNext:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  // The form content
  const formContent = (
    <>
      <h2 className="text-xl font-semibold mb-6">Hvor bor dere?</h2>
      

      {/* Save confirmation */}
      {showSaveConfirmation && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-green-800 font-medium">Lagret: </span>
            <span className="text-green-700">
              {kommune && `${kommune} kommune`}
              {kommune && schoolStage && ', '}
              {schoolStage && schoolStage}
            </span>
          </div>
        </div>
      )}

      <div className="block mb-4 relative">
        <label className="block mb-1">Kommune *</label>
        <input
          ref={kommuneInputRef}
          type="text"
          value={kommuneInput}
          onChange={e => handleKommuneInputChange(e.target.value)}
          onKeyDown={handleKommuneKeyDown}
          onFocus={() => kommuneInput && kommuneSuggestions.length > 0 && setShowKommuneDropdown(true)}
          className={`w-full p-2 border rounded ${kommune ? 'border-green-500 bg-green-50' : ''}`}
          placeholder="Begynn å skrive kommunenavnet..."
          autoComplete="off"
        />
        {kommune && (
          <div className="absolute right-3 top-9 text-green-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {showKommuneDropdown && kommuneSuggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            {kommuneSuggestions.map((k, index) => (
              <button
                key={k}
                type="button"
                onClick={() => handleKommuneSelect(k)}
                className={`w-full text-left px-4 py-2 hover:bg-primary-50 transition-colors ${
                  index === highlightedIndex ? 'bg-primary-100' : ''
                } ${k === kommune ? 'text-primary font-medium' : ''}`}
              >
                {k}
              </button>
            ))}
          </div>
        )}
        {kommuneInput && !kommune && kommuneSuggestions.length === 0 && (
          <p className="text-sm text-error-500 mt-1">Ingen kommune funnet. Prøv å skrive et annet navn.</p>
        )}
      </div>

      <label className="block mb-4">
        Skolenivå *
        <select 
          value={schoolStage} 
          onChange={e => setSchoolStage(e.target.value)} 
          className="w-full p-2 border rounded mt-1"
        >
          <option value="">Velg skolenivå</option>
          {stages.map(s => <option key={s}>{s}</option>)}
        </select>
      </label>

    </>
  );

  // For embedded mode, just return the form content
  if (embedded) {
    return (
      <div className="border-b pb-8">
        {formContent}
      </div>
    );
  }

  // For wizard mode, wrap in WizardLayout
  return (
    <WizardLayout
      step={2}
      total={5}
      nextDisabled={!kommune || !schoolStage}
      onNext={onNext}
    >
      {formContent}
    </WizardLayout>
  );
}
